import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ShoppingBag, CheckCircle2, Truck, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BrandMark } from "@/components/Brand";

// Paste the Amazon listing URL here when it's ready — the button appears automatically.
const AMAZON_URL = "";

const money = (n) => `$${Number(n).toFixed(2)}`;

const fieldCls =
  "w-full bg-white border border-[#E3D7C3] px-4 py-3 text-sm text-[#3B2F25] placeholder:text-[#B6A48C] rounded-sm focus:border-[#C05824] transition-colors";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: "easeOut" },
});

export default function BookPage() {
  const [params, setParams] = useSearchParams();
  const [book, setBook] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", address1: "", address2: "", city: "", state: "", zip: "", country: "US" });
  const [order, setOrder] = useState(null);
  const [paid, setPaid] = useState(false);

  const sessionId = params.get("session_id");

  useEffect(() => {
    api.get("/books/the-grace-between-us").then((r) => setBook(r.data)).catch(() => toast.error("Could not load book details"));
  }, []);

  // Returning from Stripe — poll until payment confirms
  useEffect(() => {
    if (params.get("cancelled")) {
      toast.info("Checkout cancelled — no charge was made.");
      setParams({}, { replace: true });
      return;
    }
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/books/orders/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") { setPaid(true); return; }
      } catch (e) { /* keep polling briefly */ }
      if (++tries < 12) setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/books/orders", { ...form, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(" ") : "Something went wrong — please try again.";
      toast.error(msg);
      setBusy(false);
    }
  };

  if (sessionId) {
    return (
      <div data-testid="order-confirmation" className="min-h-screen bg-parchment flex items-center justify-center p-6">
        <motion.div {...fade()} className="max-w-lg w-full bg-white border border-[#E3D7C3] rounded-md p-8 md:p-12 text-center shadow-[0_20px_60px_-20px_rgba(90,60,30,0.25)]">
          {!paid ? (
            <>
              <Loader2 className="mx-auto text-ember animate-spin" size={36} />
              <h1 className="font-serif-book text-3xl text-[#3B2F25] mt-6">Confirming your order…</h1>
              <p className="text-[#8A7458] text-sm mt-3">We're verifying your payment with our secure processor. This takes a few seconds.</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="mx-auto text-[#2E4B34]" size={44} />
              <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-ember mt-6">Order Confirmed</p>
              <h1 className="font-serif-book text-4xl text-[#3B2F25] mt-3">Thank you, {order.customer_name.split(" ")[0]}.</h1>
              <p className="text-[#8A7458] text-sm mt-4 leading-relaxed">
                Your copy of <em className="font-serif-book text-base">The Grace Between Us</em> is being prepared with care.
                A confirmation is on its way to <span className="text-[#3B2F25]">{order.email}</span>.
              </p>
              <div className="mt-8 bg-parchment border border-[#E3D7C3] rounded-sm p-5 text-left text-sm">
                <div className="flex justify-between py-1"><span className="text-[#8A7458]">Order number</span><span data-testid="order-number" className="font-mono2 text-[#3B2F25]">{order.id}</span></div>
                <div className="flex justify-between py-1"><span className="text-[#8A7458]">Paperback</span><span className="text-[#3B2F25]">{money(order.amount_book)}</span></div>
                <div className="flex justify-between py-1"><span className="text-[#8A7458]">Shipping</span><span className="text-[#3B2F25]">{money(order.amount_shipping)}</span></div>
                <div className="flex justify-between py-1 border-t border-[#E3D7C3] mt-2 pt-2"><span className="text-[#8A7458] font-semibold">Total paid</span><span className="text-[#3B2F25] font-semibold">{money(order.amount_total)}</span></div>
                <p className="text-[#8A7458] text-xs mt-4">
                  Ships to: {order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                </p>
              </div>
              <Link to="/books/the-grace-between-us" onClick={() => setParams({}, { replace: true })}
                className="inline-block mt-8 font-mono2 text-[11px] uppercase tracking-[0.2em] text-ember hover:text-[#3B2F25] transition-colors">
                ← Back to the book
              </Link>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="min-h-screen bg-parchment text-[#3B2F25]">
      {/* minimal header */}
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark size={26} />
          <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A7458]">UMG Media</span>
        </Link>
        <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A7458] hidden sm:block">New Release</span>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start mt-4 md:mt-10">
          {/* Cover */}
          <motion.div {...fade(0)} className="mx-auto w-full max-w-sm md:max-w-none">
            <div className="relative">
              <div className="absolute -inset-3 bg-parchment-2 rounded-md rotate-[-1.5deg]" />
              <img data-testid="book-cover" src="/grace-between-us-cover.webp"
                alt="The Grace Between Us — A Christian Novel by Jane Ball"
                className="relative w-full rounded-md shadow-[0_30px_70px_-20px_rgba(90,60,30,0.45)]" />
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-[#8A7458]">
              <span className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-[0.15em]"><Truck size={14} /> Ships in the US</span>
              <span className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-[0.15em]"><ShieldCheck size={14} /> Secure checkout</span>
            </div>
          </motion.div>

          {/* Details */}
          <div className="md:pt-6">
            <motion.p {...fade(0.05)} className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-ember">A Christian Novel</motion.p>
            <motion.h1 {...fade(0.1)} data-testid="book-title" className="font-serif-book font-semibold text-5xl md:text-6xl leading-[1.05] mt-4">
              The Grace<br />Between Us
            </motion.h1>
            <motion.div {...fade(0.15)} className="flex items-center gap-3 mt-5">
              <span className="h-px w-10 bg-[#D9C6A8]" />
              <Heart size={14} className="text-ember" />
              <span className="h-px w-10 bg-[#D9C6A8]" />
            </motion.div>
            <motion.p {...fade(0.2)} className="font-serif-book italic text-2xl text-[#6B5741] mt-5">by Jane Ball</motion.p>

            <motion.p {...fade(0.25)} data-testid="book-description" className="text-[#6B5741] leading-relaxed mt-6 text-[15px] md:text-base">
              {book?.description || "…"}
            </motion.p>

            {book && (
              <motion.div {...fade(0.3)} className="mt-8 bg-white border border-[#E3D7C3] rounded-md p-6">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#8A7458]">Paperback</span>
                  <span data-testid="book-price" className="font-serif-book text-3xl text-[#3B2F25]">{money(book.price)}</span>
                </div>
                <div className="flex items-baseline justify-between mt-2">
                  <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#8A7458]">Shipping</span>
                  <span className="text-sm text-[#6B5741]">{money(book.shipping)}</span>
                </div>
                <div className="h-px bg-[#EFE4CF] my-5" />
                <div className="flex flex-col sm:flex-row gap-3">
                  <button data-testid="buy-paperback-btn" onClick={() => { setShowForm(true); setTimeout(() => document.getElementById("order-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); }}
                    className="flex-1 bg-ember text-[#FBF6EE] font-mono2 text-xs uppercase tracking-[0.2em] px-6 py-4 rounded-sm hover:bg-[#A84A1D] transition-colors flex items-center justify-center gap-2">
                    <ShoppingBag size={15} /> Buy Paperback — {money(book.price + book.shipping)}
                  </button>
                  {AMAZON_URL && (
                    <a data-testid="buy-amazon-btn" href={AMAZON_URL} target="_blank" rel="noopener noreferrer"
                      className="flex-1 border border-[#3B2F25] text-[#3B2F25] font-mono2 text-xs uppercase tracking-[0.2em] px-6 py-4 rounded-sm hover:bg-[#3B2F25] hover:text-[#FBF6EE] transition-colors text-center">
                      Buy on Amazon
                    </a>
                  )}
                </div>
                <p className="text-[11px] text-[#B6A48C] mt-4 text-center">Sold and shipped directly by UMG Media. Secure payment by Stripe.</p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Checkout form */}
        {showForm && book && (
          <motion.section id="order-form" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto mt-16 md:mt-24">
            <div className="text-center mb-8">
              <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-ember">Almost Yours</p>
              <h2 className="font-serif-book text-4xl mt-3">Where should we send it?</h2>
            </div>
            <form data-testid="order-form-fields" onSubmit={submit} className="bg-white border border-[#E3D7C3] rounded-md p-6 md:p-8 flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="order-name" required minLength={2} placeholder="Full name" value={form.name} onChange={set("name")} className={fieldCls} />
                <input data-testid="order-email" required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={fieldCls} />
              </div>
              <input data-testid="order-address1" required minLength={3} placeholder="Street address" value={form.address1} onChange={set("address1")} className={fieldCls} />
              <input data-testid="order-address2" placeholder="Apt, suite, etc. (optional)" value={form.address2} onChange={set("address2")} className={fieldCls} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <input data-testid="order-city" required minLength={2} placeholder="City" value={form.city} onChange={set("city")} className={`${fieldCls} col-span-2`} />
                <input data-testid="order-state" required minLength={2} placeholder="State" value={form.state} onChange={set("state")} className={fieldCls} />
                <input data-testid="order-zip" required minLength={3} placeholder="ZIP" value={form.zip} onChange={set("zip")} className={fieldCls} />
              </div>
              <div className="bg-parchment border border-[#E3D7C3] rounded-sm p-4 text-sm mt-2">
                <div className="flex justify-between py-0.5"><span className="text-[#8A7458]">The Grace Between Us — Paperback</span><span>{money(book.price)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-[#8A7458]">Shipping</span><span>{money(book.shipping)}</span></div>
                <div className="flex justify-between pt-2 mt-1 border-t border-[#E3D7C3] font-semibold"><span>Total</span><span data-testid="order-total">{money(book.price + book.shipping)}</span></div>
              </div>
              <button data-testid="checkout-submit" type="submit" disabled={busy}
                className="bg-ember text-[#FBF6EE] font-mono2 text-xs uppercase tracking-[0.2em] px-6 py-4 rounded-sm hover:bg-[#A84A1D] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 mt-2">
                {busy ? <><Loader2 size={15} className="animate-spin" /> Redirecting to secure payment…</> : <>Continue to Secure Payment — {money(book.price + book.shipping)}</>}
              </button>
              <p className="text-[11px] text-[#B6A48C] text-center">You'll enter your card details on Stripe's secure page. We never see or store your card.</p>
            </form>
          </motion.section>
        )}
      </main>

      <footer className="border-t border-[#E3D7C3] py-8 text-center">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#B6A48C]">© {new Date().getFullYear()} UMG Media · The Grace Between Us by Jane Ball</p>
      </footer>
    </div>
  );
}
